import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Accordion, Switch, InputField, Select, SelectOption } from '@ets/design-system';

import { useEffectOnUpdate } from '_common/hooks';
import { MeasureInput } from '_common/components';
import { MeasureInputProps } from '_common/components/MeasureInput/MeasureInput';

import { convertToDataUnit, convertToDisplayUnit, DisplayUnit } from '../utils';
import { useTableProperties } from '../TablePropertiesContext';

import styles from './Section.module.scss';

type SizeProps = Pick<Editor.Edition.TableProperties, 'width' | 'height'> & SectionProps;

type UnitOption = SelectOption & { value: DisplayUnit };
const UNIT_OPTIONS: UnitOption[] = [
  { value: 'cm', label: 'cm' },
  { value: 'in', label: 'in' },
  { value: 'pct', label: '%' },
];

const MAX_ABSOLUTE_WIDTH = 1583.8;

const Size = ({ width, height, updateData }: SizeProps) => {
  const intl = useIntl();
  const { pageSize, setHasValidWidth, preferedUnit, setPreferedUnit } = useTableProperties();
  let min: number | undefined = undefined;
  let max: number | undefined = undefined;

  const getUnitOption = () => {
    const defaultOption = UNIT_OPTIONS[0];

    if (!width) {
      return defaultOption;
    }

    if (width.value.t === 'abs') {
      //abs (pt) unit is displayed as cm (value will be parsed from pt to cm)
      return UNIT_OPTIONS.find((option) => option.value === preferedUnit) ?? defaultOption;
    } else {
      return UNIT_OPTIONS.find((option) => option.value === width.value.t) ?? defaultOption;
    }
  };

  const [currentUnit, setCurrentUnit] = useState<UnitOption | null>(getUnitOption());
  const [localWidth, setLocalWidth] = useState(
    convertToDisplayUnit(width?.value.v, width?.value.t, preferedUnit),
  );
  const [localHeight, setLocalHeight] = useState(
    convertToDisplayUnit(height?.value.v, 'abs', preferedUnit),
  );

  //#region Auto width&height management
  const [autoWidth, setAutoWidth] = useState(width?.value.t === 'auto');
  useEffect(() => {
    if (!currentUnit) {
      return;
    }

    if (
      !width ||
      (width?.value.t === 'auto' && autoWidth) ||
      (width.value.t !== 'auto' && !autoWidth)
    ) {
      return;
    }

    updateData({
      property: 'width',
      value: {
        value: autoWidth
          ? {
              v: 0,
              t: 'auto',
            }
          : {
              v: convertToDataUnit(localWidth, currentUnit.value),
              t:
                currentUnit.value === 'cm' || currentUnit.value === 'in'
                  ? 'abs'
                  : currentUnit.value,
            },
      },
    });
  }, [width, autoWidth]);

  const [autoHeight, setAutoHeight] = useState(height?.value.t === 'auto');
  useEffect(() => {
    if (
      !height ||
      (height?.value.t === 'auto' && autoHeight) ||
      (height.value.t !== 'auto' && !autoHeight)
    ) {
      return;
    }

    updateData({
      property: 'height',
      value: {
        value: autoHeight
          ? {
              v: 0,
              t: 'auto',
            }
          : {
              v: convertToDataUnit(localHeight, 'cm'),
              t: 'abs',
            },
      },
    });
  }, [height, autoHeight]);
  //#endregion

  useEffectOnUpdate(() => {
    if (width?.value.t === 'abs' && currentUnit?.value !== preferedUnit) {
      setLocalWidth(convertToDisplayUnit(width?.value.v, 'abs', preferedUnit));
      setCurrentUnit(UNIT_OPTIONS.find((option) => option.value === preferedUnit) ?? null);
    }
    setLocalHeight(convertToDisplayUnit(height?.value.v, 'abs', preferedUnit));
  }, [preferedUnit]);

  const [widthFeedback, setWidthFeedback] = useState<string | null>(null);
  useEffect(() => {
    setHasValidWidth(!widthFeedback);
  }, [widthFeedback]);

  useEffect(() => {
    //Validate to define width's input field feedback
    if (pageSize) {
      switch (currentUnit?.value) {
        case 'cm':
        case 'in':
          {
            const convertedMaxAbsoluteWidth = convertToDisplayUnit(
              MAX_ABSOLUTE_WIDTH,
              'abs',
              currentUnit.value,
            );

            // update min and max values for external usage (e.g. MeasureInput)
            max = convertedMaxAbsoluteWidth;
            min = 0;

            setWidthFeedback(
              localWidth < 0 || localWidth > convertedMaxAbsoluteWidth
                ? intl.formatMessage({ id: 'ONLY_VALID_BETWEEN_MIN_AND_MAX' }, { min, max })
                : null,
            );
          }
          break;
        case 'pct':
          // update min and max values for external usage (e.g. MeasureInput)
          max = 100;
          min = 1;

          setWidthFeedback(
            localWidth < 1 || localWidth > 100
              ? intl.formatMessage({ id: 'ONLY_VALID_BETWEEN_MIN_AND_MAX' }, { min, max })
              : null,
          );
          break;
      }
    }
  }, [localWidth, currentUnit]);

  const handleWidthChange: MeasureInputProps['onChange'] = (newValue) => {
    setLocalWidth(+newValue);

    if (currentUnit) {
      updateData({
        property: 'width',
        value: {
          value: {
            v: convertToDataUnit(+newValue, currentUnit.value),
            t: currentUnit.value === 'cm' || currentUnit.value === 'in' ? 'abs' : currentUnit.value,
          },
        },
      });
    }
  };

  const handleUnitChange = (newUnit: UnitOption) => {
    if (newUnit.value === currentUnit?.value) {
      return;
    }

    setCurrentUnit((prevUnit) => {
      let newWidth;
      let skipUpdate = false;

      switch (prevUnit?.value) {
        case 'auto':
          newWidth =
            newUnit.value !== 'auto' && newUnit.value !== 'pct'
              ? (convertToDisplayUnit(pageSize, 'abs', newUnit.value) ?? 0)
              : 100;
          break;
        case 'cm':
        case 'in':
          switch (newUnit.value) {
            case 'cm':
            case 'in':
              newWidth = convertToDisplayUnit(width?.value.v, 'abs', newUnit.value);
              skipUpdate = true;
              break;
            case 'pct':
              newWidth =
                newUnit.value === 'pct' && pageSize
                  ? +(
                      (+localWidth * 100) /
                      convertToDisplayUnit(pageSize, 'abs', prevUnit.value)
                    ).toFixed(2)
                  : 0 /*Unexpected unit or undefined pageSize*/;
              break;
          }
          break;
        case 'pct':
          if (newUnit.value === 'cm' || newUnit.value === 'in') {
            newWidth =
              pageSize && (newUnit.value === 'cm' || newUnit.value === 'in')
                ? +(
                    (+localWidth * convertToDisplayUnit(pageSize, 'abs', newUnit.value)) /
                    100
                  ).toFixed(2)
                : 0 /*Unexpected unit or undefined pageSize*/;
          }
          break;
      }

      if (newWidth) {
        setLocalWidth(newWidth);

        /**
         * Changing between 'cm' and 'in' shouldn't update width property
         * Because no change to the value itself has been made and skipping avoids rounding unnecessarily
         */
        if (!skipUpdate) {
          updateData({
            property: 'width',
            value: {
              value: {
                v: convertToDataUnit(newWidth, newUnit.value),
                t: newUnit.value === 'cm' || newUnit.value === 'in' ? 'abs' : newUnit.value,
              },
            },
          });
        }
      }

      if (newUnit.value === 'cm' || newUnit.value === 'in') {
        setPreferedUnit(newUnit.value);
      }
      return newUnit;
    });
  };

  const handleHeightChange: MeasureInputProps['onChange'] = (newValue) => {
    setLocalHeight(+newValue);

    if (currentUnit && currentUnit.value !== 'pct') {
      updateData({
        property: 'height',
        value: {
          value: {
            v: convertToDataUnit(+newValue, preferedUnit),
            t: autoHeight ? 'auto' : 'abs',
          },
        },
      });
    }
  };

  const handleAutoWidthToggle = () => setAutoWidth((prev) => !prev);
  const handleAutoHeightToggle = () => setAutoHeight((prev) => !prev);

  const renderAutoWidthSwitch = () => (
    <Switch
      size="xs"
      value={autoWidth}
      onChange={handleAutoWidthToggle}
      testId="tableProperties-auto-width"
      label={intl.formatMessage({ id: 'AUTO_WIDTH' })}
      labelPlacement="right"
      style={{ paddingTop: '1rem' }}
    />
  );

  return (
    <Accordion
      size="m"
      variant="standard"
      title={intl.formatMessage({ id: 'SIZE' })}
      defaultOpen={true}
      testId="tableProperties-size"
    >
      <div className={styles.root}>
        {width && (
          <div className={styles.column}>
            <InputField
              size="m"
              label={intl.formatMessage({ id: 'WIDTH' })}
              feedbackContent={widthFeedback ?? undefined}
              testId="tableProperties-width"
              disabled={autoWidth}
            >
              <div className={`${styles.row} ${styles.flexChildren}`}>
                <MeasureInput
                  size="m"
                  value={`${localWidth}`}
                  disabled={autoWidth}
                  placeholder=""
                  onChange={handleWidthChange}
                  avoidEmpty
                  min={min}
                  max={max}
                  testId="tableProperties-width"
                />
                <Select
                  variant="standard"
                  size="m"
                  value={currentUnit}
                  options={UNIT_OPTIONS}
                  disabled={autoWidth}
                  isClearable={false}
                  menuWidth="auto"
                  onChange={(newValue) => {
                    if (newValue) {
                      handleUnitChange(newValue as UnitOption);
                    }
                  }}
                  testId="width-unit"
                />
              </div>
            </InputField>
            {height && renderAutoWidthSwitch()}
          </div>
        )}
        <div className={styles.column}>
          {height ? (
            <InputField
              size="m"
              label={intl.formatMessage({ id: 'HEIGHT' })}
              testId="tableProperties-height"
              disabled={autoHeight}
            >
              <div className={`${styles.row} ${styles.flexChildren}`}>
                <MeasureInput
                  size="m"
                  value={`${localHeight}`}
                  disabled={autoHeight}
                  onChange={handleHeightChange}
                  placeholder=""
                  valueSuffix={preferedUnit}
                  avoidEmpty
                  testId="tableProperties-height"
                />
              </div>
            </InputField>
          ) : (
            <div className={`${styles.column} ${styles.autoSwitch}`}>{renderAutoWidthSwitch()}</div>
          )}
        </div>
        {!width && (
          <div className={`${styles.column} ${styles.autoSwitch}`}>
            <Switch
              size="xs"
              value={autoHeight}
              onChange={handleAutoHeightToggle}
              testId="tableProperties-auto-height"
              label={intl.formatMessage({ id: 'AUTO_HEIGHT' })}
              labelPlacement="right"
            />
          </div>
        )}
      </div>
    </Accordion>
  );
};

export default Size;
